import { LegalCheckboxes, LegalText, apiClient } from '@/api';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

import { termsAcceptedSignalState } from '@/state/project';

const REQUIRED_CHECKBOXES: Array<keyof LegalCheckboxes> = [
  'authorized_user',
  'written_authorization',
  'terminal_responsibility'
];

const CHECKBOX_LABELS: Record<keyof LegalCheckboxes, string> = {
  authorized_user:
    'I am an Authorized User of my organization, acting within my assigned role.',
  written_authorization:
    'I will obtain and retain explicit written authorization for every target before initiating any active testing module.',
  terminal_responsibility:
    'I accept full responsibility for every command issued through any Crimson Raven terminal — including AI-proposed commands I approve.'
};

type Props = {
  onAccepted: () => void;
};

export default function DisclaimerModal({ onAccepted }: Props) {
  const [disclaimer, setDisclaimer] = useState<LegalText | null>(null);
  const [terminal, setTerminal] = useState<LegalText | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checkboxes, setCheckboxes] = useState<LegalCheckboxes>({
    authorized_user: false,
    written_authorization: false,
    terminal_responsibility: false
  });
  const [submitting, setSubmitting] = useState(false);
  const bumpTermsAccepted = useSetRecoilState(termsAcceptedSignalState);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiClient.getLegalDisclaimerText(),
      apiClient.getLegalTerminalText()
    ])
      .then(([d, t]) => {
        if (cancelled) return;
        setDisclaimer(d);
        setTerminal(t);
      })
      .catch((err: Error) => {
        if (!cancelled)
          setLoadError(err.message || 'Failed to load disclaimer');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const allChecked = REQUIRED_CHECKBOXES.every((k) => checkboxes[k]);

  const handleAccept = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    try {
      const result = await apiClient.acceptLegal(checkboxes);
      if (result.accepted) {
        bumpTermsAccepted((n) => n + 1);
        onAccepted();
      } else {
        toast.error('Acceptance was not recorded — please try again.');
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to record acceptance'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheck = (key: keyof LegalCheckboxes, value: boolean) => {
    setCheckboxes((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] flex flex-col gap-0 p-0 [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-6 pb-3 border-b">
          <DialogTitle>Crimson Raven — Operator Disclaimer</DialogTitle>
          <DialogDescription>
            Read both notices, tick all three checkboxes, then click Accept to
            continue. This disclaimer is shown again only when its content is
            updated.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 text-sm leading-relaxed [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-2 [&_li]:mb-1 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:px-2 [&_td]:py-1">
          {!disclaimer && !terminal && !loadError && (
            <div className="text-muted-foreground">Loading disclaimer…</div>
          )}
          {loadError && (
            <div className="text-destructive">
              Failed to load disclaimer: {loadError}
            </div>
          )}
          {disclaimer && (
            <section data-testid="disclaimer-section">
              <ReactMarkdown>{disclaimer.text}</ReactMarkdown>
            </section>
          )}
          {terminal && (
            <section
              className="mt-6 pt-6 border-t"
              data-testid="terminal-section"
            >
              <ReactMarkdown>{terminal.text}</ReactMarkdown>
            </section>
          )}
        </div>

        <div className="border-t p-6 space-y-3">
          {REQUIRED_CHECKBOXES.map((key) => (
            <label key={key} className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={checkboxes[key]}
                onCheckedChange={(v) => handleCheck(key, Boolean(v))}
                aria-label={CHECKBOX_LABELS[key]}
                data-testid={`checkbox-${key}`}
              />
              <span className="text-sm leading-snug">
                {CHECKBOX_LABELS[key]}
              </span>
            </label>
          ))}
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleAccept}
              disabled={!allChecked || submitting}
              data-testid="accept-button"
            >
              {submitting ? 'Recording…' : 'Accept and Continue'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
